import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { publicGet, publicPost } from "@/lib/publicApi";
import WhatsAppCta from "@/components/WhatsAppCta";

/** Platform AI Support Assistant — floating widget for marketing pages.
 *  Grounded on backend `/api/public/support-chat` (packages + FAQs + CMS).
 *
 *  Contract:
 *    - Fire-and-forget conversation, session_id kept in-memory only.
 *    - Welcome message is fetched from /support-chat/welcome (server-side
 *      localises via the browser's Accept-Language header) so a Hindi/
 *      Marathi/Tamil-etc visitor sees a native greeting.
 *    - When backend returns `needs_contact:true`, we render an inline
 *      form for name/phone/email; on submit we send an empty message
 *      with the structured contact block so the server can escalate.
 *    - When backend returns `escalation.ticket_id`, we surface a
 *      badge + link so the user knows a real ticket was raised.
 *    - Respects prefers-reduced-motion (no bounce/pulse on the FAB).
 */
const SUGGESTED = [
    "Which package should I choose?",
    "How does the 50/50 payment work?",
    "Can AI invent qualifications on my profile?",
    "Talk to a human",
];

const FALLBACK_WELCOME = "Hi! I'm the DDIP support assistant. Ask me about packages, onboarding, features or refunds — or type 'human' to reach our team.";

export default function PlatformSupportChat() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);          // {role, content, ticket?}
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState("");
    const [busy, setBusy] = useState(false);
    const [needsContact, setNeedsContact] = useState(false);
    const [contact, setContact] = useState({ name: "", phone: "", email: "" });
    const [welcome, setWelcome] = useState(FALLBACK_WELCOME);
    const bodyRef = useRef(null);

    // Auto-scroll on new messages.
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages, open]);

    // Fetch localised welcome once. Fire-and-forget; on failure the
    // English fallback is fine.
    useEffect(() => {
        publicGet("/support-chat/welcome")
            .then((r) => { if (r?.welcome) setWelcome(r.welcome); })
            .catch(() => { });
    }, []);

    // First-open welcome message.
    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{ role: "assistant", content: welcome }]);
        }
    }, [open, welcome]);

    async function send(text, extraContact) {
        const trimmed = (text || "").trim();
        if (!trimmed && !extraContact) return;
        if (trimmed) {
            setMessages((m) => [...m, { role: "user", content: trimmed }]);
        }
        setInput("");
        setBusy(true);
        try {
            const r = await publicPost("/support-chat", {
                message: trimmed || "(providing my contact info)",
                session_id: sessionId,
                contact: extraContact || undefined,
            });
            if (r.session_id) setSessionId(r.session_id);
            setNeedsContact(!!r.needs_contact);
            setMessages((m) => [...m, {
                role: "assistant",
                content: r.reply,
                ticket: r.escalation?.ticket_id || null,
            }]);
        } catch (e) {
            setMessages((m) => [...m, {
                role: "assistant",
                content: "I hit a snag reaching the server. Please retry or use the Help page.",
            }]);
        } finally {
            setBusy(false);
        }
    }

    async function submitContact(e) {
        e?.preventDefault?.();
        if (!contact.name.trim() || (!contact.phone.trim() && !contact.email.trim())) return;
        await send(
            `My name is ${contact.name}${contact.phone ? ", phone " + contact.phone : ""}${contact.email ? ", email " + contact.email : ""}`,
            { name: contact.name, phone: contact.phone, email: contact.email },
        );
        setContact({ name: "", phone: "", email: "" });
    }

    return (
        <>
            {/* Floating launcher */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#0F766E] hover:bg-[#0b615a] text-white shadow-xl shadow-[#0F766E]/30 flex items-center justify-center transition-transform hover:scale-105"
                    aria-label="Open support chat"
                    data-testid="platform-chat-launcher"
                >
                    <MessageCircle size={22} />
                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#B8862B] ring-2 ring-white" />
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    className="fixed bottom-5 right-5 z-50 w-[92vw] sm:w-[380px] max-h-[86vh] flex flex-col rounded-2xl overflow-hidden bg-white shadow-2xl shadow-black/25 border border-[#123B5D]/10"
                    data-testid="platform-chat-panel"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#123B5D] to-[#0F766E] text-white px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
                                <Sparkles size={16} />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-bold truncate">DDIP Support Assistant</div>
                                <div className="text-[10px] text-white/70">AI-powered · escalates to a human</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                            data-testid="platform-chat-close"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Body */}
                    <div
                        ref={bodyRef}
                        className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#F5F8FA]"
                        data-testid="platform-chat-messages"
                    >
                        {messages.map((m, i) => (
                            <div key={i}
                                className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}
                                data-testid={`platform-chat-msg-${m.role}-${i}`}>
                                <div className={
                                    "max-w-[85%] rounded-2xl px-3 py-2 text-[13.5px] leading-relaxed shadow-sm " +
                                    (m.role === "user"
                                        ? "bg-[#123B5D] text-white rounded-br-sm"
                                        : "bg-white text-[#17212B] rounded-bl-sm border border-[#123B5D]/10")
                                }>
                                    {m.content}
                                    {m.ticket && (
                                        <div className="mt-2 space-y-2" data-testid="platform-chat-ticket">
                                            <div className="text-[11px] text-[#0F766E] font-semibold">
                                                Ticket raised · #{m.ticket.slice(0, 8).toUpperCase()}
                                            </div>
                                            <WhatsAppCta variant="inline"
                                                label="Prefer WhatsApp? Message us"
                                                prefill={`Hi, I just raised support ticket #${m.ticket.slice(0, 8).toUpperCase()} on doctorsprofile.in.`} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start" data-testid="platform-chat-typing">
                                <div className="max-w-[70%] rounded-2xl px-3 py-2 bg-white text-[#17212B]/60 text-xs border border-[#123B5D]/10">
                                    Typing…
                                </div>
                            </div>
                        )}

                        {/* Suggested chips — only before the first user turn */}
                        {messages.filter((m) => m.role === "user").length === 0 && !busy && (
                            <div className="pt-1 flex flex-wrap gap-1.5" data-testid="platform-chat-suggested">
                                {SUGGESTED.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="text-[11.5px] bg-white border border-[#123B5D]/15 hover:border-[#0F766E]/40 hover:text-[#0F766E] text-[#17212B]/80 rounded-full px-2.5 py-1 transition-colors"
                                        data-testid={`platform-chat-suggest-${s.slice(0, 12).replace(/\W/g, "-")}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Contact form (escalation) */}
                    {needsContact && (
                        <form onSubmit={submitContact}
                            className="px-3 py-2 bg-[#FFF8EC] border-t border-[#B8862B]/30 space-y-2"
                            data-testid="platform-chat-contact-form">
                            <div className="text-[11px] text-[#123B5D] font-semibold">Share your contact so we can reach you</div>
                            <input
                                required
                                className="w-full px-2.5 py-1.5 text-[12.5px] border border-[#B8862B]/40 rounded-md"
                                placeholder="Your name"
                                value={contact.name}
                                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                                data-testid="platform-chat-contact-name"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    className="w-full px-2.5 py-1.5 text-[12.5px] border border-[#B8862B]/40 rounded-md"
                                    placeholder="Phone"
                                    value={contact.phone}
                                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                                    data-testid="platform-chat-contact-phone"
                                />
                                <input
                                    type="email"
                                    className="w-full px-2.5 py-1.5 text-[12.5px] border border-[#B8862B]/40 rounded-md"
                                    placeholder="Email"
                                    value={contact.email}
                                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                    data-testid="platform-chat-contact-email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy || !contact.name.trim() || (!contact.phone.trim() && !contact.email.trim())}
                                className="w-full text-[12.5px] bg-[#B8862B] hover:bg-[#a3771d] text-white font-semibold py-1.5 rounded-md disabled:opacity-60"
                                data-testid="platform-chat-contact-submit"
                            >
                                Send my details
                            </button>
                        </form>
                    )}

                    {/* Input */}
                    <form
                        onSubmit={(e) => { e.preventDefault(); send(input); }}
                        className="px-3 py-2 bg-white border-t border-[#123B5D]/10 flex items-center gap-2"
                        data-testid="platform-chat-form"
                    >
                        <input
                            className="flex-1 text-[13px] border border-[#123B5D]/15 focus:border-[#0F766E] focus:outline-none rounded-full px-3 py-2"
                            placeholder="Ask about pricing, onboarding, refunds…"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={busy}
                            maxLength={1000}
                            data-testid="platform-chat-input"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            className="h-9 w-9 rounded-full bg-[#0F766E] hover:bg-[#0b615a] text-white flex items-center justify-center disabled:opacity-60"
                            aria-label="Send"
                            data-testid="platform-chat-send"
                        >
                            <Send size={15} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
